import { applyUpload } from 'src/design/organisms/attachments/attachmentEdits';
import {
  IAttachedFile,
  IAttachmentChange,
} from 'src/design/organisms/attachments/attachmentTypes';
import {
  IAttachmentUpload,
  useAttachmentUpload,
} from 'src/design/organisms/attachments/useAttachmentUpload';

export interface IUseControlledAttachmentsOptions {
  files: IAttachedFile[];
  maxFiles: number;
  uploadFile: (file: File) => Promise<IAttachedFile>;
  downloadFile: (id: string) => Promise<{ url: string; name: string }>;
  onChange: (files: IAttachedFile[], change: IAttachmentChange) => void;
}

export interface IControlledAttachments {
  /** True when maxFiles caps the list at one file, i.e. an upload replaces rather than appends. */
  singleSlot: boolean;
  /** Capacity left before the list is full, floor-clamped at 0. */
  remaining: number;
  upload: IAttachmentUpload;
}

/** @registry Shared FileDrop/ImageUpload wiring: derives singleSlot/remaining from maxFiles and files.length, and folds a completed upload into the controlled list via applyUpload before calling the caller's onChange. */
export const useControlledAttachments = ({
  files,
  maxFiles,
  uploadFile,
  downloadFile,
  onChange,
}: IUseControlledAttachmentsOptions): IControlledAttachments => {
  const singleSlot = maxFiles === 1;
  const remaining = Math.max(0, maxFiles - files.length);

  const upload = useAttachmentUpload({
    uploadFile,
    downloadFile,
    onUploaded: (uploaded) => {
      const next = applyUpload(files, uploaded, singleSlot);
      onChange(next.files, next.change);
    },
  });

  return { singleSlot, remaining, upload };
};
